import React, { useCallback, useEffect} from 'react';
import { IView } from 'Framework';
import { strings } from 'src/localization';
import * as css from './AddVehicle.module.scss';
import { ApptCust, ApptVeh, Customer, NewVehicle, RDWModels, SpecData } from 'src/models';
import DesktopView from '../AddVehicle/DesktopView/DesktopView';
import MobileView from '../AddVehicle/MobileView/containers/MobileView';
import AddVehicleFooter from '../AddVehicle/AddVehicleFooter/containers/AddVehicleFooter';
import MediaQuery from 'react-responsive';
import { ScrollBar } from '@framework-controls/core';
import { getProviderId, sendSdEvent } from 'src/helpers/ShiftDigitalHelper/ShiftDigitalHelper';

interface IProps {
    custInfo?: Customer;
    newVehicleInfo?: NewVehicle;
    vehicleSpecs?: SpecData;
    onAddNewVehicle: (state: NewVehicle) => void;
    leftPaneViewOrder: IView[];
    addLogEntry: (action: string, screen: string, details: string) => void;
    selectedVehicle?: ApptVeh;
    updateVehicleMileage: (mileage: number) => void,
    prevMileage: number,
    apptCust: ApptCust,
    sameFirstNames: boolean;
    currentProperty: string;
    addVehicle: boolean;
    theme: string;
    defaultLen: number;
    multipleCustomers: boolean;
    rdwModels?: RDWModels[];
    Itype: string | undefined;
    TMNAMileage: number;
    sessionId: string;
    lexusMCLaunched: string;
    toyotaMCLaunched: string;
    lexusTDMCLaunched: string;
    toyotaTDMCLaunched: string;
    identityVerified: boolean;
}

const AddVehicle = (props: IProps) => {
    const { onAddNewVehicle, leftPaneViewOrder, vehicleSpecs, newVehicleInfo, selectedVehicle} = props;

    const newVINentryFlag = vehicleSpecs!.newVINentryFlag;
    const estMileageChangedHandler = useCallback((_id: string, value: string) => {
        const options = { ...newVehicleInfo! };
        const strValue = value.toString();
        if (strValue.length <= 7) {
            const numericString = strValue?.replace(/,/g, '');
            let newMileage = parseFloat(numericString);
            if (isNaN(newMileage)) {
                newMileage = 0;
            }
            const formattedMileage = numericString ? parseFloat(numericString).toLocaleString('en-US') : '';
            options['estMileage'] = !newMileage ? '' : formattedMileage;
            if (value === 'Unknown') {
                options['estMileage'] = value;
            }
        }
        onAddNewVehicle({ ...options });
    }, [newVehicleInfo, onAddNewVehicle]);

    const isSDAnalyticsEnabled =
        props.vehicleSpecs?.toyotaTrackingFlag === 'Y' ||
        props.vehicleSpecs?.lexusTrackingFlag === 'Y' ||
        props.vehicleSpecs?.mbzTrackingFlag === 'Y' ||
        props.vehicleSpecs?.mazdaCATrackingFlag === 'Y' ||
        props.vehicleSpecs?.vwTrackingFlag === 'Y' ||
        props.vehicleSpecs?.jaguarTrackingFlag === 'Y' ||
        props.vehicleSpecs?.landRoverTrackingFlag === 'Y';
    const isVW = props.vehicleSpecs?.vwTrackingFlag === 'Y';
    let greetingMessage = <></>;
    let header = '';
    let subHeader = '';

    if ((props.custInfo?.login && !props.addVehicle && !props.newVehicleInfo) || props.currentProperty === 'color' ||
        (props.selectedVehicle?.make !== '' && props.newVehicleInfo?.make === '') ||
        (props.newVehicleInfo?.year !== '' && props.newVehicleInfo?.make !== '' && props.newVehicleInfo?.model === '' &&
        props?.rdwModels?.length === 0 && props.currentProperty === 'model')) {
        subHeader = strings.UpdateMileage;
    } else {
        subHeader = strings.WhichVehicleAddingAppt;
    }

    if (props.apptCust.custFirstName === '') {
        header = strings.FirstVisit;
        subHeader = (props.currentProperty === 'color' ||
            (props.currentProperty === 'model' &&
            (!props.rdwModels || props.rdwModels.length === 0))) ?
            ` ${strings.GladToHaveYou}` :
            `${strings.GladToHaveYou} ${strings.WhichVehicle}`;
    } else if (!props.identityVerified ||
        (props.multipleCustomers && !props.sameFirstNames)) {
        header = strings.WelcomeBack;
    } else {
        header = props.apptCust.custFirstName + ',';
    }

    useEffect(() => {
        // Add an 'Add Vehicle' log entry if an existing customer is adding a vehicle or
        // if a new customer reaches this page.
        if (header === strings.FirstVisit) {
            // Add log for new customer
            props.addLogEntry('Screen Entry', 'Add Vehicle', '');
            if (isSDAnalyticsEnabled) {
                sendSdEvent('scheduleServiceVehicleShown', {
                    scheduleServiceSessionId: props.sessionId,
                    scheduleServiceProvider: getProviderId(props.vehicleSpecs, props.toyotaMCLaunched,
                        props.toyotaTDMCLaunched, props.lexusMCLaunched, props.lexusTDMCLaunched),
                }, {oncePerSession: isVW});
            }
        } else if (props.addVehicle) {
            // Add log for existing customer adding a vehicle
            props.addLogEntry('Screen Entry', 'Add Vehicle', '');
        }
    }, [header, props.addLogEntry, props.addVehicle, props.sessionId,
        props.toyotaMCLaunched, props.toyotaTDMCLaunched, props.lexusMCLaunched, props.lexusTDMCLaunched,
        props.vehicleSpecs, isSDAnalyticsEnabled, isVW]);

    greetingMessage = (
        <div>
            <h2 className={
                props.theme === 'mazda' ? css.headerMazda :
                    props.theme === 'genesis' ? css.headerGenesis :
                        undefined
            }>{header}</h2>
            <h3>{subHeader}</h3>
        </div>
    );

    let body = '';
    if ((props.newVehicleInfo?.make || props.selectedVehicle?.make) &&
        props.vehicleSpecs?.newVINentryFlag !== 'R' && window.innerWidth <= 600) {
        body = css.bodywithcollapse;
    } else {
        body = css.body;
    }

    return (
        <React.Fragment>
            <ScrollBar scrollVertical>
                <div className={body}>
                    {greetingMessage}
                    <hr></hr>
                    <MediaQuery maxWidth={600}>
                        <div className={css.mobileView}>
                            <MobileView
                                optionsSelected={newVehicleInfo}
                                estMileageChangedHandler={estMileageChangedHandler}
                                vinSpecFlag={newVINentryFlag}
                                selectedVehicle={selectedVehicle!}
                                updateVehicleMileage={props.updateVehicleMileage}
                                prevMileage={props?.prevMileage?.toString()}
                            />
                        </div>
                    </MediaQuery>
                    <MediaQuery minWidth={601}>
                        <div className={css.desktopView}>
                            <DesktopView
                                optionsSelected={newVehicleInfo}
                                vinSpecFlag={newVINentryFlag}
                                leftPaneViewOrder={leftPaneViewOrder}
                                selectedVehicle={selectedVehicle!}
                                updateVehicleMileage={props.updateVehicleMileage}
                                prevMileage={props?.prevMileage?.toString()}
                                theme={props.theme}
                                defaultLen={props.defaultLen}
                                Itype={props?.Itype}
                                TMNAMileage={props.TMNAMileage}
                            />
                        </div>
                    </MediaQuery>
                </div>
            </ScrollBar>
            <MediaQuery minWidth={601}>
                <AddVehicleFooter
                    estMilChange={estMileageChangedHandler}
                    selectedVehicle={selectedVehicle!}
                />
            </MediaQuery>
            <MediaQuery maxWidth={600}>
                <AddVehicleFooter
                    estMilChange={estMileageChangedHandler}
                    selectedVehicle={selectedVehicle!}
                />
            </MediaQuery>
        </React.Fragment>
    );
};

export default AddVehicle;
